import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '../constants/analytics';
import { getAnalytics } from '../services/FirebaseInit';

// Initialize the analytics module for the current platform
const analytics = getAnalytics();

const ANONYMOUS_USER_ID_KEY = '@anonymous_user_id';
let sessionId = uuidv4();
let anonymousUserId: string | null = null;
let customerId: string | null = null;
let lastScreenName: string | null = null;

/**
 * Initialize analytics by setting up anonymous ID and session ID
 */
export const initAnalytics = async () => {
  try {
    // Get or create anonymous user ID
    anonymousUserId = await AsyncStorage.getItem(ANONYMOUS_USER_ID_KEY);
    if (!anonymousUserId) {
      anonymousUserId = uuidv4();
      await AsyncStorage.setItem(ANONYMOUS_USER_ID_KEY, anonymousUserId);
      console.log('✨ [Analytics] Created new anonymous user ID:', anonymousUserId);
    } else {
      console.log('ℹ️ [Analytics] Found existing anonymous user ID:', anonymousUserId);
    }

    console.log(`🚀 [Analytics] Initializing for ${Platform.OS} (Session: ${sessionId})`);

    // Set initial user properties
    // Note: wrapping in try-catch to prevent initialization failures from blocking the app
    try {
      await analytics().setAnalyticsCollectionEnabled(true);
      await analytics().setUserProperties({
        [ANALYTICS_PARAMS.ANONYMOUS_USER_ID]: anonymousUserId,
        [ANALYTICS_PARAMS.PLATFORM]: Platform.OS,
        [ANALYTICS_PARAMS.SESSION_ID]: sessionId,
      });
      console.log('✅ [Analytics] Initial user properties set');
    } catch (firebaseError) {
      console.error('⚠️ [Analytics] Firebase-specific initialization error:', firebaseError);
    }
  } catch (error) {
    console.error('❌ [Analytics] Critical initialization error:', error);
  }
};

/**
 * Get common parameters for all events
 */
const getCommonParams = () => {
  return {
    [ANALYTICS_PARAMS.ANONYMOUS_USER_ID]: anonymousUserId,
    [ANALYTICS_PARAMS.CUSTOMER_ID]: customerId,
    [ANALYTICS_PARAMS.PLATFORM]: Platform.OS,
    [ANALYTICS_PARAMS.SESSION_ID]: sessionId,
    [ANALYTICS_PARAMS.CLICKSTREAM_ID]: sessionId, // Using session_id as clickstream_id for now
    [ANALYTICS_PARAMS.TIMESTAMP]: new Date().toISOString(),
    [ANALYTICS_PARAMS.REFERRER]: lastScreenName,
    [ANALYTICS_PARAMS.REF_MARKER]: lastScreenName,
  };
};

/**
 * Log a custom event to Firebase Analytics
 * @param eventName Name of the event (no spaces, use underscores)
 * @param params Optional object with event parameters
 */
export const logEvent = async (eventName: string, params?: { [key: string]: any }) => {
  try {
    if (!anonymousUserId) await initAnalytics();

    // Firebase event names must be alphanumeric and underscores only
    const sanitizedEventName = eventName.replace(/[^a-zA-Z0-9_]/g, '_');
    
    const eventParams = {
      ...getCommonParams(),
      ...params,
    };

    if (__DEV__) {
      console.log(`📊 [Analytics] Logging event: ${sanitizedEventName}`, eventParams);
    }
    
    await analytics().logEvent(sanitizedEventName, eventParams);
  } catch (error) {
    console.error(`❌ [Analytics] Failed to log event ${eventName}:`, error);
  }
};

/**
 * Log a screen view to Firebase Analytics
 * @param screenName Name of the screen
 * @param screenClass Class name of the screen
 */
export const logScreenView = async (screenName: string, screenClass: string) => {
  try {
    if (!anonymousUserId) await initAnalytics();

    // Log standard screen view
    await analytics().logScreenView({
      screen_name: screenName,
      screen_class: screenClass,
    });

    // Log the specific page_viewed event as per requirements
    await logEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
      [ANALYTICS_PARAMS.PAGE_NAME]: screenName,
    });

    // Update last screen for referrer tracking
    lastScreenName = screenName;
  } catch (error) {
    console.error('Failed to log screen view:', error);
  }
};

/**
 * Log a UI click event
 * @param elementName Name of the element clicked
 * @param extraParams Optional additional parameters
 */
export const logUIClick = async (elementName: string, extraParams?: { [key: string]: any }) => {
  try {
    await logEvent(ANALYTICS_EVENTS.UI_CLICKED, {
      [ANALYTICS_PARAMS.ELEMENT_NAME]: elementName,
      [ANALYTICS_PARAMS.PAGE_NAME]: lastScreenName || 'unknown',
      ...extraParams,
    });
  } catch (error) {
    console.error(`Failed to log UI click for ${elementName}:`, error);
  }
};

/**
 * Set user properties for Firebase Analytics
 * @param properties Object with user properties
 */
export const setUserProperties = async (properties: { [key: string]: any }) => {
  try {
    await analytics().setUserProperties(properties);
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

/**
 * Set user ID for Firebase Analytics and link anonymous ID
 * @param id Unique identifier for the user (customer_id)
 */
export const setUserId = async (id: string | null) => {
  try {
    if (!anonymousUserId) await initAnalytics();
    
    customerId = id;
    await analytics().setUserId(id);
    
    if (id) {
      // Link anonymous_user_id to customer_id in user properties
      await analytics().setUserProperties({
        [ANALYTICS_PARAMS.CUSTOMER_ID]: id,
        [ANALYTICS_PARAMS.ANONYMOUS_USER_ID]: anonymousUserId,
      });
      
      // Log an association event
      await logEvent(ANALYTICS_EVENTS.USER_AUTH_ASSOCIATION, {
        [ANALYTICS_PARAMS.CUSTOMER_ID]: id,
        [ANALYTICS_PARAMS.ANONYMOUS_USER_ID]: anonymousUserId,
      });
    }
  } catch (error) {
    console.error('Failed to set user ID:', error);
  }
};

/**
 * Event Signup / Booking Funnel Events
 */

/**
 * Log when an event page is viewed
 */
export const logEventPageViewed = async (eventId: string, eventType: string, pageName: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_PAGE_VIEWED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.PAGE_NAME]: pageName,
  });
};

/**
 * Log when event signup is started
 */
export const logEventSignupStarted = async (eventId: string, eventType: string, pageName: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_SIGNUP_STARTED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.PAGE_NAME]: pageName,
  });
};

/**
 * Log when date picker is viewed
 */
export const logEventDatePickerViewed = async (eventId: string, eventType: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_DATE_PICKER_VIEWED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
  });
};

/**
 * Log when a date is selected
 */
export const logEventDateSelected = async (eventId: string, eventType: string, selectedDate: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_DATE_SELECTED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.SELECTED_DATE]: selectedDate,
  });
};

/**
 * Log when checkout page is viewed
 */
export const logEventCheckoutViewed = async (eventId: string, eventType: string, price: number, currency: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_CHECKOUT_VIEWED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.PRICE]: price,
    [ANALYTICS_PARAMS.CURRENCY]: currency,
  });
};

/**
 * Log when payment is started
 */
export const logEventPaymentStarted = async (eventId: string, eventType: string, paymentMethod: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_PAYMENT_STARTED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.PAYMENT_METHOD]: paymentMethod,
  });
};

/**
 * Log when payment succeeds
 */
export const logEventPaymentSucceeded = async (eventId: string, eventType: string, amountPaid: number, currency: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_PAYMENT_SUCCEEDED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.AMOUNT_PAID]: amountPaid,
    [ANALYTICS_PARAMS.CURRENCY]: currency,
  });
};

/**
 * Log when payment fails
 */
export const logEventPaymentFailed = async (eventId: string, eventType: string, errorCode: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_PAYMENT_FAILED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.ERROR_CODE]: errorCode || 'unknown_error',
  });
};

/**
 * Log when signup is completed
 */
export const logEventSignupCompleted = async (eventId: string, eventType: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_SIGNUP_COMPLETED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
  });
};

/**
 * Log when signup is abandoned
 */
export const logEventSignupAbandoned = async (eventId: string, eventType: string, lastStep: string) => {
  await logEvent(ANALYTICS_EVENTS.EVENT_SIGNUP_ABANDONED, {
    [ANALYTICS_PARAMS.EVENT_ID]: eventId,
    [ANALYTICS_PARAMS.EVENT_TYPE]: eventType,
    [ANALYTICS_PARAMS.LAST_STEP]: lastStep,
  });
};
